import type { BasketShotImpact } from './three/basketSceneLayout';
import { basketAudioPresentation } from './three/basketArenaPresentation';

const MUTE_STORAGE_KEY = 'odevlendir-basket-muted';

export class BasketAudio {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private compressor: DynamicsCompressorNode | null = null;
  private reverb: ConvolverNode | null = null;
  private reverbGain: GainNode | null = null;
  private ambience: AudioBufferSourceNode | null = null;
  private muted = this.readMuted();

  private readMuted() {
    try { return window.localStorage.getItem(MUTE_STORAGE_KEY) === 'true'; } catch { return false; }
  }

  private ensureContext() {
    if (this.context && this.master) {
      if (this.context.state === 'suspended') void this.context.resume();
      return this.context;
    }
    const context = new AudioContext();
    const master = context.createGain();
    const compressor = context.createDynamicsCompressor();
    compressor.threshold.value = -18;
    compressor.knee.value = 18;
    compressor.ratio.value = 4;
    compressor.attack.value = 0.004;
    compressor.release.value = 0.2;
    master.gain.value = this.muted ? 0 : basketAudioPresentation.masterGain;
    master.connect(compressor).connect(context.destination);

    const reverb = context.createConvolver();
    reverb.buffer = this.createImpulse(context);
    const reverbGain = context.createGain();
    reverbGain.gain.value = 0.24;
    reverb.connect(reverbGain).connect(master);

    this.context = context;
    this.master = master;
    this.compressor = compressor;
    this.reverb = reverb;
    this.reverbGain = reverbGain;
    this.startArenaAmbience();
    return context;
  }

  private createImpulse(context: AudioContext) {
    const length = Math.floor(context.sampleRate * basketAudioPresentation.reverbSeconds);
    const impulse = context.createBuffer(2, length, context.sampleRate);
    for (let channel = 0; channel < impulse.numberOfChannels; channel += 1) {
      const data = impulse.getChannelData(channel);
      for (let index = 0; index < length; index += 1) {
        const envelope = (1 - index / length) ** basketAudioPresentation.reverbDecay;
        data[index] = (Math.random() * 2 - 1) * envelope;
      }
    }
    return impulse;
  }

  private createNoiseBuffer(context: AudioContext, duration: number) {
    const length = Math.max(1, Math.floor(context.sampleRate * duration));
    const buffer = context.createBuffer(1, length, context.sampleRate);
    const data = buffer.getChannelData(0);
    let previous = 0;
    for (let index = 0; index < length; index += 1) {
      const white = Math.random() * 2 - 1;
      previous = previous * 0.18 + white * 0.82;
      data[index] = previous;
    }
    return buffer;
  }

  private connect(output: AudioNode, wet = 0.15) {
    if (!this.master) return;
    output.connect(this.master);
    if (this.reverb && wet > 0) {
      const send = this.context!.createGain();
      send.gain.value = wet;
      output.connect(send).connect(this.reverb);
    }
  }

  private tone(
    frequency: number,
    duration: number,
    type: OscillatorType,
    gain: number,
    delay = 0,
    endFrequency = frequency,
    wet = 0.12,
  ) {
    const context = this.ensureContext();
    const startsAt = context.currentTime + delay;
    const oscillator = context.createOscillator();
    const filter = context.createBiquadFilter();
    const volume = context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, startsAt);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, endFrequency), startsAt + duration);
    filter.type = 'lowpass';
    filter.frequency.value = Math.min(12_000, Math.max(900, frequency * 6));
    volume.gain.setValueAtTime(0.0001, startsAt);
    volume.gain.exponentialRampToValueAtTime(gain, startsAt + 0.008);
    volume.gain.exponentialRampToValueAtTime(0.0001, startsAt + duration);
    oscillator.connect(filter).connect(volume);
    this.connect(volume, wet);
    oscillator.start(startsAt);
    oscillator.stop(startsAt + duration + 0.03);
  }

  private noise(
    duration: number,
    gain: number,
    filterType: BiquadFilterType,
    startFrequency: number,
    endFrequency: number,
    delay = 0,
    wet = 0.12,
    flutter = false,
  ) {
    const context = this.ensureContext();
    const startsAt = context.currentTime + delay;
    const source = context.createBufferSource();
    const filter = context.createBiquadFilter();
    const volume = context.createGain();
    source.buffer = this.createNoiseBuffer(context, duration + 0.02);
    filter.type = filterType;
    filter.Q.value = filterType === 'bandpass' ? 1.5 : 0.7;
    filter.frequency.setValueAtTime(startFrequency, startsAt);
    filter.frequency.exponentialRampToValueAtTime(Math.max(40, endFrequency), startsAt + duration);
    volume.gain.setValueAtTime(0.0001, startsAt);
    volume.gain.exponentialRampToValueAtTime(gain, startsAt + 0.012);
    if (flutter) {
      volume.gain.setValueAtTime(gain * 0.42, startsAt + duration * 0.25);
      volume.gain.setValueAtTime(gain * 0.88, startsAt + duration * 0.42);
      volume.gain.setValueAtTime(gain * 0.3, startsAt + duration * 0.62);
    }
    volume.gain.exponentialRampToValueAtTime(0.0001, startsAt + duration);
    source.connect(filter).connect(volume);
    this.connect(volume, wet);
    source.start(startsAt);
    source.stop(startsAt + duration + 0.03);
  }

  private startArenaAmbience() {
    const context = this.context;
    if (!context || !this.master || this.ambience) return;
    const source = context.createBufferSource();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();
    source.buffer = this.createNoiseBuffer(context, 2.4);
    source.loop = true;
    filter.type = 'bandpass';
    filter.frequency.value = 310;
    filter.Q.value = 0.55;
    gain.gain.value = basketAudioPresentation.ambienceGain;
    source.connect(filter).connect(gain).connect(this.master);
    source.start();
    this.ambience = source;
  }

  unlock() { this.ensureContext(); }
  isMuted() { return this.muted; }

  toggleMuted() {
    this.muted = !this.muted;
    const context = this.ensureContext();
    this.master?.gain.setTargetAtTime(this.muted ? 0 : basketAudioPresentation.masterGain, context.currentTime, 0.025);
    try { window.localStorage.setItem(MUTE_STORAGE_KEY, String(this.muted)); } catch { /* storage is optional */ }
    return this.muted;
  }

  shotRelease() {
    this.unlock();
    this.tone(118, 0.11, 'sine', 0.12, 0, 78, 0.08);
    this.noise(0.34, 0.055, 'bandpass', 1_300, 4_800, 0.025, 0.18);
  }

  impact(impact: BasketShotImpact, streak: number) {
    if (impact === 'swish') this.swish(streak);
    else if (impact === 'backboard') this.backboardMiss();
    else this.rimMiss();
  }

  private swish(streak: number) {
    const duration = basketAudioPresentation.swishDuration;
    this.noise(duration, 0.19, 'bandpass', 6_800, 1_850, 0, 0.32, true);
    this.noise(duration * 0.8, 0.065, 'highpass', 3_800, 1_200, 0.025, 0.18, true);
    this.tone(196, 0.16, 'sine', 0.055, 0.03, 147, 0.28);
    const lift = Math.min(0.095, 0.055 + streak * 0.008);
    this.noise(basketAudioPresentation.crowdLiftDuration, lift, 'bandpass', 520, 820, 0.06, 0.38, true);
    [659.25, 783.99, 987.77].forEach((frequency, index) => {
      this.tone(frequency, 0.22, 'triangle', 0.038 + Math.min(streak, 4) * 0.004, 0.08 + index * 0.065, frequency * 1.015, 0.34);
    });
  }

  private rimMiss() {
    [468, 713, 1_062].forEach((frequency, index) => {
      this.tone(frequency, 0.28 - index * 0.035, index === 0 ? 'triangle' : 'sine', 0.1 / (index + 1), index * 0.006, frequency * 0.88, 0.44);
    });
    this.noise(0.08, 0.095, 'highpass', 5_800, 2_600, 0, 0.25);
    this.wrongCue(0.11);
  }

  private backboardMiss() {
    this.tone(104, 0.21, 'sine', 0.16, 0, 58, 0.3);
    this.tone(286, 0.12, 'triangle', 0.075, 0.005, 174, 0.4);
    this.noise(0.12, 0.13, 'bandpass', 1_900, 620, 0, 0.33);
    this.wrongCue(0.13);
  }

  private wrongCue(delay: number) {
    this.tone(294, 0.22, 'triangle', 0.045, delay, 247, 0.28);
    this.tone(220, 0.28, 'sine', 0.04, delay + 0.105, 165, 0.3);
  }

  finish(perfect = false) {
    const notes = perfect ? [523.25, 659.25, 783.99, 1_046.5] : [392, 523.25, 659.25, 783.99];
    notes.forEach((frequency, index) => this.tone(frequency, 0.42, 'triangle', 0.055, index * 0.105, frequency * 1.01, 0.42));
    this.noise(1.25, perfect ? 0.11 : 0.075, 'bandpass', 430, 760, 0.08, 0.5, true);
  }

  dispose() {
    try { this.ambience?.stop(); } catch { /* already stopped */ }
    this.ambience = null;
    void this.context?.close();
    this.context = null;
    this.master = null;
    this.compressor = null;
    this.reverb = null;
    this.reverbGain = null;
  }
}
